// Reads and writes of capable-to-promise (spec 03 §3.7, §4.9, §5 rows 74 to
// 78), all through the generated client. Every queryFn passes its signal and
// every write carries a fresh Idempotency-Key.

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { refreshQuery, shouldRetryRead, type ReadContext } from "../../../../lib/query";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";

type S = components["schemas"];
export type CtpPromise = S["CtpPromise"];
export type CtpPromiseDetail = S["CtpPromiseDetail"];
export type CtpPromiseList = S["CtpPromiseListResponse"];
export type CtpQuoteRequest = S["CtpQuoteRequest"];
export type CtpQuoteResult = S["CtpQuoteResult"];
export type PromiseBasis = S["PromiseBasis"];
export type PromiseBasisVerdict = S["PromiseBasisVerdict"];
export type PromiseStatus = CtpPromise["status"];
export type FulfillmentPath = CtpPromise["fulfillment_path"];
export type PromiseSource = CtpPromise["source"];
export type RiskReason = NonNullable<CtpPromise["risk_reason"]>;
type PromiseListQuery = NonNullable<paths["/v1/promises"]["get"]["parameters"]["query"]>;

export const PROMISE_STATUSES: readonly PromiseStatus[] = [
  "quoted",
  "committed",
  "at_risk",
  "broken",
  "kept",
  "withdrawn",
  "expired",
];

/** The statuses the list reads when the address names none (§5 row 77's default). */
export const LIVE_PROMISE_STATUSES: readonly PromiseStatus[] = ["quoted", "committed", "at_risk"];

export const FULFILLMENT_PATHS: readonly FulfillmentPath[] = [
  "offcut",
  "stock",
  "production",
  "transfer",
];

/** The list's page size. */
export const PROMISE_PAGE = 50;
/** The longest withdraw reason (contracts `CtpWithdrawRequest.reason`). */
export const PROMISE_REASON_MAX = 500;

/** The list filters of §5 row 77, as the screen holds them. */
export interface PromiseFilters {
  readonly status?: PromiseStatus | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly customer?: string | undefined;
  readonly product?: string | undefined;
  readonly order?: string | undefined;
}

export const promiseKeys = {
  customers: (q: string) => ["planning", "promise-customers", q] as const,
  orders: (facilityId: string, customerId: string) =>
    ["planning", "promise-orders", facilityId, customerId] as const,
  all: (facilityId: string) => ["planning", "promises", facilityId] as const,
  list: (facilityId: string, filters: PromiseFilters, cursor: string | undefined) =>
    ["planning", "promises", facilityId, "list", filters, cursor ?? null] as const,
  one: (id: string) => ["planning", "promise", id] as const,
};

/** The query row 77 takes for the screen's filters. */
export function promiseListQuery(
  facilityId: string,
  filters: PromiseFilters,
  cursor: string | undefined,
): PromiseListQuery {
  const query: PromiseListQuery = { facility_id: facilityId, limit: PROMISE_PAGE };
  if (filters.status !== undefined) query.status = filters.status;
  if (filters.from !== undefined) query.promised_from = filters.from;
  if (filters.to !== undefined) query.promised_to = filters.to;
  if (filters.customer !== undefined) query.customer_id = filters.customer;
  if (filters.product !== undefined) query.product_id = filters.product;
  if (filters.order !== undefined) query.sales_order_id = filters.order;
  if (cursor !== undefined) query.cursor = cursor;
  return query;
}

/** One page of the facility's promises, newest promised date first (row 77). */
export function usePromises(
  facilityId: string,
  filters: PromiseFilters,
  cursor: string | undefined,
) {
  return useQuery({
    queryKey: promiseKeys.list(facilityId, filters, cursor),
    enabled: facilityId.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext): Promise<CtpPromiseList> =>
      unwrap(
        await client.GET("/v1/promises", {
          signal,
          params: { query: promiseListQuery(facilityId, filters, cursor) },
        }),
        "promises",
      ),
  });
}

// ---- the workbench tile (spec 03 §4.1, J-3) ---------------------------------------

/** One read of the tile asks for at most this many rows (row 77's ceiling). */
export const PROMISE_TILE_LIMIT = 200;

/** What the promises tile shows: counts with an "at least" flag, and the next dates. */
export interface PromiseTileFacts {
  readonly atRisk: number;
  readonly atRiskMore: boolean;
  readonly broken: number;
  readonly brokenMore: boolean;
  readonly upcoming: readonly CtpPromise[];
  readonly upcomingMore: boolean;
}

/**
 * The tile's three reads of row 77 (spec 03 §4.1's promises tile): the
 * facility's at-risk promises, its broken ones judged in the last 30 days, and
 * its live promises dated from today to seven days on, soonest first. Row 77
 * has no count, so each read takes up to 200 rows and says "at least" past
 * that. A broken promise counts by when it was judged (`resolved_at`), and the
 * list is newest promised date first, so 200 rows reach back past 30 days
 * unless more than 200 broke inside them.
 */
export function usePromiseTile(facilityId: string, today: string, nowMs: number) {
  return useQuery({
    queryKey: ["planning", "promises", facilityId, "tile", today] as const,
    enabled: facilityId.length > 0 && today.length === 10,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext): Promise<PromiseTileFacts> => {
      const read = async (query: PromiseListQuery) =>
        unwrap(await client.GET("/v1/promises", { signal, params: { query } }), "promises");
      const base = { facility_id: facilityId, limit: PROMISE_TILE_LIMIT };
      const weekOn = addDays(today, 7);
      const [risk, broken, upcoming] = await Promise.all([
        read({ ...base, status: "at_risk" }),
        read({ ...base, status: "broken" }),
        read({
          ...base,
          status: ["committed", "at_risk"],
          promised_from: today,
          promised_to: weekOn,
        }),
      ]);
      const since = nowMs - 30 * 24 * 3600 * 1000;
      const brokenRecent = broken.items.filter((p) => {
        const at = p.resolved_at ?? p.updated_at;
        return Date.parse(at) >= since;
      });
      return {
        atRisk: risk.items.length,
        atRiskMore: risk.next_cursor !== null,
        broken: brokenRecent.length,
        brokenMore: broken.next_cursor !== null && brokenRecent.length === broken.items.length,
        upcoming: [...upcoming.items].sort((a, b) =>
          a.promised_date === b.promised_date
            ? a.promise_no.localeCompare(b.promise_no)
            : a.promised_date < b.promised_date
              ? -1
              : 1,
        ),
        upcomingMore: upcoming.next_cursor !== null,
      };
    },
  });
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The orders the order filter offers: at most this many, newest confirmed first. */
export const PROMISE_ORDER_CHOICES = 50;

/**
 * Customers for the list's customer filter, through module 09's customer
 * door (spec 03 §4.9), so a sales reader finds only the customers it is
 * assigned. Asked once the person has typed two characters.
 */
export function usePromiseCustomerSearch(q: string) {
  return useQuery({
    queryKey: promiseKeys.customers(q),
    enabled: q.length >= 2,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext) =>
      unwrap(
        await client.GET("/v1/customers", { signal, params: { query: { q, limit: 10 } } }),
        "customers",
      ).items,
  });
}

/** The chosen customer's orders at the facility, for the order filter (spec 03 §4.9). */
export function usePromiseCustomerOrders(facilityId: string, customerId: string | undefined) {
  return useQuery({
    queryKey: promiseKeys.orders(facilityId, customerId ?? ""),
    enabled: facilityId.length > 0 && customerId !== undefined,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext) =>
      unwrap(
        await client.GET("/v1/orders", {
          signal,
          params: {
            query: {
              customer_id: customerId ?? "",
              facility_id: facilityId,
              limit: PROMISE_ORDER_CHOICES,
            },
          },
        }),
        "orders",
      ).items,
  });
}

/** One promise with its timeline (row 78). */
export function usePromise(id: string | undefined) {
  return useQuery({
    queryKey: promiseKeys.one(id ?? ""),
    enabled: id !== undefined && id.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext): Promise<CtpPromiseDetail> =>
      unwrap(
        await client.GET("/v1/promises/{id}", { signal, params: { path: { id: id ?? "" } } }),
        "promise",
      ),
  });
}

// ---- writes ---------------------------------------------------------------------

/** Row 74: a quote, which holds nothing and expires in 72 hours. */
export async function quotePromise(body: CtpQuoteRequest): Promise<CtpQuoteResult> {
  return unwrap(
    await client.POST("/v1/promises/quote", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "promise quote",
  );
}

/** Row 75: commit a quoted promise onto an order line, judged on fresh facts. */
export async function commitPromise(
  id: string,
  salesOrderLineId: string | undefined,
): Promise<CtpPromise> {
  return unwrap(
    await client.POST("/v1/promises/{id}/commit", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: salesOrderLineId !== undefined ? { sales_order_line_id: salesOrderLineId } : {},
    }),
    "promise commit",
  );
}

/** Row 76: withdraw a promise with a person's reason. */
export async function withdrawPromise(id: string, reason: string): Promise<CtpPromise> {
  return unwrap(
    await client.POST("/v1/promises/{id}/withdraw", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { reason },
    }),
    "promise withdraw",
  );
}

/** Re-reads the facility's promise reads, and the named promises, after a write. */
export function usePromiseRefresh() {
  const qc = useQueryClient();
  return useCallback(
    async (facilityId: string, ids: readonly string[] = []) => {
      await Promise.all([
        refreshQuery(qc, promiseKeys.all(facilityId)),
        ...ids.map((id) => refreshQuery(qc, promiseKeys.one(id))),
      ]);
    },
    [qc],
  );
}
